import { useState } from "react";
import { Input } from "@/components/base/Input.tsx";
import { customer as customerActions } from "@/repositories/customer";
import { generator as generatorActions } from "@/repositories/generator";
import { CreateCustomerDto, CreateGeneratorDto } from "@/models";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import Papa from "papaparse";
import { Button } from "@/components/base/Button.tsx";
import shortUUID from "short-uuid";

export const ImportPage = () => {
  const [tenantId, setTenantId] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
  };
  const handleImport = async () => {
    if (!file) {
      alert("Please select a file first");
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      // eslint-disable-next-line
      complete: async function (results: { data: any }) {
        const rows = results.data;
        for (const row of rows) {
          // Map CSV row to the customer and generator types
          const customerId = shortUUID.generate();
          const generatorId = shortUUID.generate();
          const customer = mapToCustomer(row, customerId, generatorId);
          const generator = mapToGenerator(row, customerId, generatorId);

          // Insert into Firestore
          await addCustomerToFirestore(customer);
          await addGeneratorToFirestore(generator);
        }
      },
      // eslint-disable-next-line
      error: function (error: any) {
        console.error("Error parsing CSV:", error);
      },
    });
  };

  // Function to map CSV row to CreateCustomerDto
  function mapToCustomer(
    // eslint-disable-next-line
    row: any,
    customerId: string,
    generatorId: string
  ): CreateCustomerDto & { id: string } {
    const name = row["Customer Names"].split(" ");
    return {
      id: customerId,
      tenantId: tenantId, // You can customize this
      firstName: name[0],
      lastName: name.length > 1 ? name[1] : "",
      notes: row["Notes"] || "",
      address: {
        address1: row["Address"],
        address2: "", // If there is a second address line, map it here
        city: "", // You can customize based on your data
        state: "", // You can customize based on your data
        zip: "", // You can customize based on your data
      },
      generatorId: generatorId,
      meterNumber: row["Meter Number"] || "",
      serviceAmp: row["Service Amp."] || "",
      phone: { phoneNumber: row["Phone"] || "", type: "home" },
      email: row["Email"] || "",
    };
  }

  // Function to map CSV row to CreateGeneratorDto
  function mapToGenerator(
    // eslint-disable-next-line
    row: any,
    customerId: string,
    generatorId: string
  ): CreateGeneratorDto & { id: string } {
    return {
      id: generatorId,
      airFilter: "", // You can customize this if needed
      battery: "", // You can customize this if needed
      batteryChangeDate: null, // Map this if applicable
      oilFilter: "", // You can customize this if needed
      tenantId: tenantId, // You can customize this
      customerId: customerId, // Link to the customer
      installedDate: parseDate(row["Date Installed"]),
      notes: row["Generator notes"] || "" + row["CC"] || "",
      kwRating: parseKw(row["kW"]),
      maintenanceKit: row["M. Kit"] || "",
      make: "Unknown", // You can customize this if you have data
      modelNumber: "Unknown", // You can customize this if you have data
      serialNumber: row["Serial Number"] || "",
      mobileLinkExp: "", // If applicable, add it here
      lastServiceDate: parseDate(row["Date Service"]),
      nextServiceDate: null, // If available, map it here
      planType: "", // You can customize this
      renewStartDate: null, // Map this if applicable
      renewEndDate: null, // Map this if applicable
      serviceAgreementPrice: 0, // You can customize this
      warranty: row["Warranty"] || "",
      warrantyExpirationDate: null, // Map this if applicable
    };
  }

  // Utility function to parse a date from the CSV (if it exists)
  function parseDate(dateStr: string): Date | null {
    try {
      return dateStr ? new Date(dateStr) : null;
      // eslint-disable-next-line
    } catch (error) {
      return null;
    }
  }

  // Utility function to parse the kW rating from the CSV
  function parseKw(kwStr: string): number | null {
    const kw = parseFloat(kwStr);
    return isNaN(kw) ? null : kw;
  }

  // Function to add customer to Firestore
  async function addCustomerToFirestore(customer: CreateCustomerDto) {
    return await customerActions.create(customer, tenantId);
  }

  // Function to add generator to Firestore
  async function addGeneratorToFirestore(generator: CreateGeneratorDto) {
    return await generatorActions.create(generator, tenantId);
  }

  return (
    <div>
      <Input value={tenantId} onChange={(e) => setTenantId(e.target.value)} />
      <Input onChange={handleFileChange} type="file" />
      <Button onClick={handleImport} disabled={tenantId === ""}>
        Import
      </Button>
    </div>
  );
};
